import { useIsRetractionOnly } from "@/hooks/use-is-retraction-only";
import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense";
import { useViewerHasAtLeastAdminRights } from "@/lib/rights/use-viewer-has-right";
import {
  LocationCardAddress,
  LocationCardContainer,
  LocationCardContent,
  LocationCardHeader,
  LocationCardHeaderIcon,
  LocationCardMapIcon,
  LocationCardTitle,
} from "@babackapp/e-commerce-components/location-card";
import useSetReturnToStoreActivation from "@babackapp/private-api-client/client/use-set-return-to-store-activation";
import { ShopReturnToStoreStoresDto } from "@babackapp/private-api-client/types";
import useTranslation from "@babackapp/translations/use-translation";
import { Button } from "@babackapp/ui/components/button";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@babackapp/ui/components/card";
import { ReturnToStoreFormValues } from "../shop-informations-settings-form/types";
import AddStoreDialog from "./add-to-store-dialog";
import ReturnToStoreForm from "./return-to-store-form";
import { StoreReturnExceptions } from "./store-return-exceptions";
import UpdateStoreDialog from "./update-store-dialog";
import { HomeIcon } from "lucide-react";
import StoreOpeningState from "@babackapp/e-commerce-components/store-opening-state";
import { Badge } from "@babackapp/ui/components/badge";
import { LockClosedIcon } from "@heroicons/react/24/solid";

export default function ReturnToStoresCard() {
  const { data: shop } = useCurrentShopSuspense();

  const { mutateAsync: setReturnToStoreActivation } =
    useSetReturnToStoreActivation(shop.id);

  const onSubmit = (values: ReturnToStoreFormValues) => {
    return setReturnToStoreActivation(values.returnToStoreActivated);
  };

  const isRetractionOnly = useIsRetractionOnly();

  if (!shop) return null;

  if (isRetractionOnly) {
    return (
      <div className="relative overflow-hidden rounded-lg border min-h-[160px]">
        {/* Mock statique */}
        <div className="pointer-events-none select-none p-6 space-y-4">
          {/* Toggle activation */}
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <div className="text-sm font-medium">
                Activer les retours en boutique
              </div>
              <div className="text-xs text-muted-foreground">
                Profiter de votre fonctionnalité Premium en activant le retour
                en boutique depuis le portail client.
              </div>
            </div>
            <div className="h-5 w-9 rounded-full bg-indigo-500 border" />
          </div>
          {/* Fausses boutiques */}
          {[
            {
              name: "Paris - Marais",
              address: "12 rue de Bretagne, Paris",
              open: true,
            },
            {
              name: "Lyon - Confluence",
              address: "2 place Nautile, Lyon",
              open: true,
            },
            {
              name: "Bordeaux",
              address: "5 cours de l'Intendance, Bordeaux",
              open: false,
            },
          ].map((store) => (
            <div
              key={store.name}
              className="flex items-start justify-between rounded-md border bg-background px-4 py-3"
            >
              <div className="flex items-start gap-3">
                <div className="mt-0.5 h-5 w-5 rounded bg-muted flex items-center justify-center">
                  <HomeIcon className="h-3 w-3 text-muted-foreground" />
                </div>
                <div className="space-y-0.5">
                  <div className="text-sm font-medium">{store.name}</div>
                  <div className="text-xs text-muted-foreground">
                    {store.address}
                  </div>
                  <div
                    className={`text-xs font-medium ${store.open ? "text-green-600" : "text-muted-foreground"}`}
                  >
                    {store.open ? "Ouvert actuellement" : "Fermé actuellement"}
                  </div>
                </div>
              </div>
            </div>
          ))}
          <div className="h-8 w-36 rounded-md border bg-muted/50" />
        </div>

        {/* Overlay avec CTA */}
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-background/70 backdrop-blur-[1.2px] px-6 text-center">
          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-muted">
            <LockClosedIcon className="h-4 w-4 text-muted-foreground" />
          </div>
          <div className="space-y-1">
            <p className="text-sm font-semibold text-foreground">
              Retours en boutique
            </p>
            <p className="text-xs text-muted-foreground max-w-sm">
              Proposez à vos clients de déposer leur retour directement dans
              l&apos;une de vos boutiques physiques.
            </p>
          </div>
          <Link
            to={`/shops/${shop.id}/settings/billing`}
            className="flex items-center gap-1.5 rounded-full bg-indigo-600 px-4 py-1.5 text-xs font-medium text-white shadow-sm"
          >
            <LockClosedIcon className="h-3 w-3" />
            Passer au plan Lite
          </Link>
        </div>
      </div>
    );
  }

  return (
    <Card>
      <CardContent className="mt-4 flex-col flex gap-4 px-0">
        <ReturnToStoreForm
          onSubmit={onSubmit}
          defaultValues={{
            returnToStoreActivated: shop.returnToStore.activated,
          }}
        />
        <StoreReturnExceptions />
        <ReturnToStoreList />
      </CardContent>
    </Card>
  );
}

function ReturnToStoreList() {
  const { data: shop } = useCurrentShopSuspense();
  const t = useTranslation();

  const hasAdminRights = useViewerHasAtLeastAdminRights();

  if (!shop.plan.returnToStore) {
    return null;
  }

  return (
    <div className="space-y-2 px-4">
      <div className="space-y-2 mb-2">
        <div role="list" className="flex flex-col gap-2">
          {shop.returnToStore.stores.map(
            (store: ShopReturnToStoreStoresDto) => (
              <StoreItem key={store.id} store={store} />
            ),
          )}
        </div>
      </div>
      {hasAdminRights && (
        <AddStoreDialog>
          <Button variant="secondary" size="sm">
            {t("settings.shop.physic.addStore")}
          </Button>
        </AddStoreDialog>
      )}
    </div>
  );
}

interface StoreItemProps {
  store: ShopReturnToStoreStoresDto;
}

function StoreItem({ store }: StoreItemProps) {
  return (
    <UpdateStoreDialog store={store}>
      <LocationCardContainer>
        <LocationCardHeader>
          <LocationCardHeaderIcon icon={HomeIcon} />
          <LocationCardTitle>
            {store.name ?? store.address.companyName}{" "}
            {!store.selectableByUser && (
              <Badge variant="outline" size="xs">
                Hidden
              </Badge>
            )}
          </LocationCardTitle>
        </LocationCardHeader>
        <LocationCardContent>
          <LocationCardAddress>
            {store.address.address1}{" "}
            {store.address.address2 && `, ${store.address.address2} `}
            {store.address.city} {store.address.country}
            <StoreOpeningState openingHours={store.openingHours} />
          </LocationCardAddress>
          {store.embedMap && <LocationCardMapIcon />}
        </LocationCardContent>
      </LocationCardContainer>
    </UpdateStoreDialog>
  );
}
